/*
 * <highlights-carousel> — behaviour for sections/highlights-carousel.liquid.
 *
 * Plain JavaScript, no libraries, no per-frame loops:
 *  - Slides are positioned with a CSS variable (--o = offset from the active slide) and animated by CSS
 *    transitions on transform/opacity only.
 *  - The autoplay progress line is a CSS animation (scaleX 0 -> 1 over --hc-duration). When it ends the
 *    carousel advances. Pausing just pauses that animation, so progress resumes exactly where it stopped.
 *  - Swiping/dragging uses pointer events on the stage (vertical scrolling is left to the browser).
 * Autoplay is suspended while: the user pressed Pause, the tab is hidden, the section is off-screen,
 * the user is dragging, or the user prefers reduced motion (then it starts paused).
 */
(function () {
  if (window.customElements.get('highlights-carousel')) return;

  var clamp = function (v, a, b) {
    return Math.min(b, Math.max(a, v));
  };

  class HighlightsCarousel extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this.stage = this.querySelector('[data-stage]');
      this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-slide]'));
      this.n = this.slides.length;
      if (!this.stage || this.n === 0) return;
      this._ready = true;

      this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-dot]'));
      // Optional per-slide videos (null when a slide has none).
      this.videos = this.slides.map(function (s) {
        return s.querySelector('[data-video]');
      });
      this.hasVideo = this.videos.some(Boolean);
      this.videos.forEach(function (v) {
        if (!v) return;
        var media = v.parentElement;
        v.addEventListener('playing', function () {
          media.classList.add('is-video-playing');
        });
        v.addEventListener('error', function () {
          media.classList.remove('is-video-playing');
        });
      });
      this.heading = this.querySelector('.hc__heading');
      this.playBtn = this.querySelector('[data-play]');
      this.labelPlay = this.dataset.labelPlay || 'Play slideshow';
      this.labelPause = this.dataset.labelPause || 'Pause slideshow';

      this.cur = 0;
      this.prevO = this.slides.map(function (_, i) {
        return i;
      });
      this.loop = this.n >= 3;
      // Tracks the same 1024px breakpoint the CSS uses. Slide positioning is identical at every
      // width (one slide visible at a time); this only re-renders on crossing it because the
      // card's own size/spacing change there.
      this.mqPeek = window.matchMedia('(min-width: 1024px)');
      this.mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.userPaused = this.dataset.autoplay !== 'true' || this.mqReduce.matches || this.n < 2;
      this.hold = { hidden: document.hidden, offscreen: true, dragging: false };
      this.drag = null;
      this.suppressClick = false;

      this.onVisibility = this.onVisibility.bind(this);
      this.onMove = this.onMove.bind(this);
      this.onUp = this.onUp.bind(this);
      this.onMedia = this.onMedia.bind(this);

      document.addEventListener('visibilitychange', this.onVisibility);
      if (this.mqPeek.addEventListener) {
        this.mqPeek.addEventListener('change', this.onMedia);
        this.mqReduce.addEventListener('change', this.onMedia);
      }

      if (this.n > 1) {
        this.stage.addEventListener('pointerdown', this.onDown.bind(this));
        // A mouse drag that starts on a link/image would otherwise become the browser's native
        // drag-and-drop and cancel our pointer events.
        this.stage.addEventListener('dragstart', function (e) {
          e.preventDefault();
        });
        this.stage.addEventListener('click', this.onClickCapture.bind(this), true);
        this.addEventListener('keydown', this.onKey.bind(this));
        if (this.playBtn) this.playBtn.addEventListener('click', this.togglePlay.bind(this));
        this.dots.forEach(
          function (dot) {
            dot.addEventListener(
              'click',
              function () {
                this.goTo(parseInt(dot.dataset.dot, 10), true);
              }.bind(this)
            );
          }.bind(this)
        );
        // The fill animation ends -> advance (only if this is still playing).
        this.addEventListener(
          'animationend',
          function (e) {
            if (e.animationName !== 'hc-fill') return;
            if (this.isRunning()) this.goTo((this.cur + 1) % this.n, false);
          }.bind(this)
        );
      }

      // Theme Editor: selecting a slide block shows it and stops autoplay so it can be edited.
      this.addEventListener(
        'shopify:block:select',
        function (e) {
          var idx = this.slides.indexOf(e.target.closest('[data-slide]'));
          if (idx > -1) {
            this.userPaused = true;
            this.goTo(idx, true);
          }
        }.bind(this)
      );

      this.render(true);
      this.updateDots();
      this.updateState();
      this.classList.add('is-ready');

      // Heading reveal: same animation as the Product Explorer / Community Impact headline
      // elsewhere in this theme (opacity + a rise, see the CSS) — revealed later on scroll (0.75
      // of it visible, up from their 0.6). Re-armed once it drops back to barely visible (0.1),
      // not literally 0: for a tall section, a normal "scroll away and come back" rarely reaches
      // exactly 100% out of view (that needs scrolling much further than feels natural), which was
      // silently leaving it stuck revealed and never replaying — this makes scrolling it back out
      // reliably reset it every time. Reduced motion shows it immediately, unanimated, exactly like
      // those sections.
      if (this.heading) {
        if (this.mqReduce.matches) {
          this.heading.classList.add('is-visible');
        } else if ('IntersectionObserver' in window) {
          this.headingInView = false;
          this.headingObserver = new IntersectionObserver(
            function (entries) {
              var en = entries[entries.length - 1];
              if (en.intersectionRatio >= 0.75 && !this.headingInView) {
                this.headingInView = true;
                this.heading.classList.add('is-visible');
              } else if (en.intersectionRatio <= 0.1 && this.headingInView) {
                this.headingInView = false;
                this.heading.classList.remove('is-visible');
              }
            }.bind(this),
            { threshold: [0, 0.1, 0.75] }
          );
          this.headingObserver.observe(this.heading);
        } else {
          // No IntersectionObserver support: never leave it permanently hidden.
          this.heading.classList.add('is-visible');
        }
      }

      if ('IntersectionObserver' in window) {
        this.stageRevealed = false;
        this.io = new IntersectionObserver(
          function (entries) {
            var en = entries[entries.length - 1];
            // The card + controls entrance: same reveal as the heading (opacity + a rise,
            // triggered at the same later 0.75 ratio, re-armed once it drops back to barely
            // visible — see the heading's own comment above for why not literally 0) — see
            // .hc__stage in the CSS.
            if (en.intersectionRatio >= 0.75 && !this.stageRevealed) {
              this.stageRevealed = true;
              this.classList.add('is-inview');
            } else if (en.intersectionRatio <= 0.1 && this.stageRevealed) {
              this.stageRevealed = false;
              this.classList.remove('is-inview');
            }
            this.hold.offscreen = en.intersectionRatio < 0.4;
            this.updateState();
          }.bind(this),
          { threshold: [0, 0.1, 0.2, 0.4, 0.75] }
        );
        this.io.observe(this);
      } else {
        this.hold.offscreen = false;
        this.classList.add('is-inview');
        this.updateState();
      }
    }

    disconnectedCallback() {
      document.removeEventListener('visibilitychange', this.onVisibility);
      window.removeEventListener('pointermove', this.onMove);
      window.removeEventListener('pointerup', this.onUp);
      window.removeEventListener('pointercancel', this.onUp);
      if (this.io) this.io.disconnect();
      if (this.headingObserver) this.headingObserver.disconnect();
      if (this.mqPeek && this.mqPeek.removeEventListener) {
        this.mqPeek.removeEventListener('change', this.onMedia);
        this.mqReduce.removeEventListener('change', this.onMedia);
      }
      this._ready = false;
    }

    /* ---------- state ---------- */
    isRunning() {
      return !this.userPaused && !this.hold.hidden && !this.hold.offscreen && !this.hold.dragging;
    }

    /* ---------- video: only the active slide plays, and only when motion is welcome ---------- */
    slideMs() {
      return parseFloat(getComputedStyle(this).getPropertyValue('--hc-slide-ms')) || 800;
    }

    syncVideos() {
      if (!this.hasVideo) return;
      var allow = !this.userPaused && !this.hold.hidden && !this.hold.offscreen && !this.mqReduce.matches;
      var self = this;
      this.videos.forEach(function (v, i) {
        if (!v) return;
        window.clearTimeout(v._pauseTimer);
        if (i === self.cur) {
          if (allow) {
            v.preload = 'auto';
            var pr = v.play();
            if (pr && pr.catch) pr.catch(function () {}); // autoplay refused: the poster image simply stays
          } else {
            v.pause();
          }
        } else if (!v.paused) {
          // Let the slide finish moving away before freezing its video.
          v._pauseTimer = window.setTimeout(function () {
            if (i !== self.cur) v.pause();
          }, self.slideMs() + 60);
        }
      });
    }

    updateState() {
      var suspended = this.hold.hidden || this.hold.offscreen || this.hold.dragging;
      this.classList.toggle('is-paused', this.userPaused);
      this.classList.toggle('is-suspended', suspended);
      this.classList.toggle('is-reduced', this.mqReduce.matches);
      if (this.stage) this.stage.setAttribute('aria-live', this.userPaused ? 'polite' : 'off');
      if (this.playBtn) {
        this.playBtn.setAttribute('aria-label', this.userPaused ? this.labelPlay : this.labelPause);
        this.playBtn.setAttribute('aria-pressed', this.userPaused ? 'false' : 'true');
      }
      this.syncVideos();
    }

    onVisibility() {
      this.hold.hidden = document.hidden;
      this.updateState();
    }

    onMedia() {
      this.render(true);
      this.updateState();
      // If reduced motion just turned on, stop re-arming the heading and show it for good.
      if (this.mqReduce.matches && this.headingObserver) {
        this.headingObserver.disconnect();
        this.headingObserver = null;
        if (this.heading) this.heading.classList.add('is-visible');
      }
    }

    togglePlay() {
      this.userPaused = !this.userPaused;
      this.updateState();
    }

    /* ---------- positioning ---------- */
    // One slide visible at a time, on every screen size: the shortest path around the loop.
    offsetFor(i) {
      var o = i - this.cur;
      if (this.loop) {
        o = ((o % this.n) + this.n) % this.n;
        if (o > this.n / 2) o -= this.n;
      }
      return o;
    }

    render(instant) {
      var self = this;
      var teleported = [];
      this.slides.forEach(function (slide, i) {
        var o = self.offsetFor(i);
        var jump = self.loop && !instant && Math.abs(o - self.prevO[i]) > self.n / 2;
        if (jump) {
          // Crossing the "back" of the loop: reposition without animating, then fade in.
          slide.classList.add('hc-tp', 'hc-hold');
          teleported.push(slide);
        }
        self.prevO[i] = o;
        slide.dataset.o = o;
        slide.style.setProperty('--o', o);
        var active = o === 0;
        slide.classList.toggle('is-active', active);
        slide.inert = !active;
        if (active) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');
      });
      if (teleported.length) {
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            teleported.forEach(function (s) {
              s.classList.remove('hc-tp');
              s.classList.remove('hc-hold');
            });
          });
        });
      }
    }

    updateDots() {
      var cur = this.cur;
      this.dots.forEach(function (dot, i) {
        var on = i === cur;
        dot.classList.toggle('is-current', on);
        if (on) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    restartProgress() {
      var dot = this.dots[this.cur];
      var fill = dot && dot.querySelector('.hc__fill');
      if (!fill) return;
      fill.style.animation = 'none';
      void fill.offsetWidth; // flush so the animation restarts from 0
      fill.style.animation = '';
    }

    goTo(i, manual) {
      i = ((i % this.n) + this.n) % this.n;
      if (i !== this.cur) {
        this.cur = i;
        this.render(false);
        this.updateDots();
      }
      this.restartProgress(); // a new selection always restarts that slide's timer from 0
      this.updateState();
    }

    /* ---------- pointer / touch dragging ---------- */
    slotWidth() {
      var gap = parseFloat(getComputedStyle(this).getPropertyValue('--hc-gap')) || 16;
      return this.stage.getBoundingClientRect().width + gap;
    }

    onDown(e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (e.target.closest('button')) return;
      this.drag = {
        id: e.pointerId,
        x: e.clientX,
        y: e.clientY,
        t: performance.now(),
        dx: 0,
        active: false,
        slot: this.slotWidth(),
        shift: parseFloat(getComputedStyle(this).getPropertyValue('--hc-text-shift')) || 0,
        lastX: e.clientX,
        lastT: performance.now(),
        v: 0,
      };
      window.addEventListener('pointermove', this.onMove, { passive: true });
      window.addEventListener('pointerup', this.onUp);
      window.addEventListener('pointercancel', this.onUp);
    }

    onMove(e) {
      var d = this.drag;
      if (!d || e.pointerId !== d.id) return;
      var dx = e.clientX - d.x;
      var dy = e.clientY - d.y;
      if (!d.active) {
        if (Math.abs(dy) > 8 && Math.abs(dy) > Math.abs(dx)) {
          this.endTracking();
          return; // vertical scroll: leave it to the browser
        }
        if (Math.abs(dx) < 6 || Math.abs(dx) < Math.abs(dy) * 1.2) return;
        d.active = true;
        this.hold.dragging = true;
        this.classList.add('is-dragging');
        this.updateState();
        try {
          this.stage.setPointerCapture(e.pointerId);
        } catch (err) {}
      }
      var now = performance.now();
      var dt = now - d.lastT;
      if (dt > 0) d.v = (e.clientX - d.lastX) / dt;
      d.lastX = e.clientX;
      d.lastT = now;
      // Without a loop (2 slides), resist dragging past the ends.
      if (!this.loop && ((this.cur === 0 && dx > 0) || (this.cur === this.n - 1 && dx < 0))) dx *= 0.3;
      d.dx = dx;
      this.style.setProperty('--hc-drag', dx + 'px');
      // The text travels a little further than the card: drag distance * (text shift / slide width).
      this.style.setProperty('--hc-tr', d.slot ? d.shift / d.slot : 0);
      // Crossfade while dragging: neighbours fade in as they approach, the active card fades out.
      // Same on every screen size: only one slide is ever visible.
      for (var i = 0; i < this.n; i++) {
        var pos = this.prevO[i] + dx / d.slot;
        this.slides[i].style.opacity = clamp(1 - Math.abs(pos), 0, 1);
        // Text fades slightly faster than its card, so it leaves early and arrives late.
        this.slides[i].style.setProperty('--hc-text-op', clamp(1 - Math.abs(pos) * 1.6, 0, 1));
      }
    }

    onUp(e) {
      var d = this.drag;
      if (!d || (e && e.pointerId !== d.id)) return;
      var wasActive = d.active;
      var dx = d.dx;
      var v = d.v;
      this.endTracking();
      if (!wasActive) return;
      this.suppressClick = true;
      window.setTimeout(
        function () {
          this.suppressClick = false;
        }.bind(this),
        0
      );
      this.hold.dragging = false;
      this.classList.remove('is-dragging');
      var threshold = d.slot * 0.2;
      var next = this.cur;
      if (dx < -threshold || v < -0.45) next = this.cur + 1;
      else if (dx > threshold || v > 0.45) next = this.cur - 1;
      if (!this.loop) next = clamp(next, 0, this.n - 1);
      // Hand back to CSS: clear the live drag and inline opacity so the slide animates to its target.
      this.style.setProperty('--hc-drag', '0px');
      this.style.setProperty('--hc-tr', 0);
      for (var i = 0; i < this.n; i++) {
        this.slides[i].style.opacity = '';
        this.slides[i].style.removeProperty('--hc-text-op');
      }
      this.goTo(next, true);
      // If it snapped back to the same slide, keep its timer running (goTo restarted it; that is fine).
    }

    endTracking() {
      this.drag = null;
      window.removeEventListener('pointermove', this.onMove);
      window.removeEventListener('pointerup', this.onUp);
      window.removeEventListener('pointercancel', this.onUp);
    }

    onClickCapture(e) {
      // A drag must not also follow the link under the finger.
      if (this.suppressClick) {
        e.preventDefault();
        e.stopPropagation();
      }
    }

    onKey(e) {
      if (this.n < 2) return;
      if (e.key === 'ArrowRight') {
        this.goTo(this.cur + 1, true);
      } else if (e.key === 'ArrowLeft') {
        this.goTo(this.cur - 1, true);
      }
    }
  }

  window.customElements.define('highlights-carousel', HighlightsCarousel);
})();
